import { EMaterialStatus } from '@shelflix/schemas/material/enums';
import type { ListMaterialFilterTypeWithValues } from '@shelflix/schemas/material/list';

export const STATUS_GROUPS = [
  { key: 'online', text: 'Online', values: [EMaterialStatus.ACTIVE, EMaterialStatus.OPERATIONALONLINE] },
  { key: 'offline', text: 'Offline', values: [EMaterialStatus.OFFLINE] },
  { key: 'maintenance', text: 'Manutenção', values: [EMaterialStatus.MAINTENANCE, EMaterialStatus.STANDBY] },
];

const SEARCH_FIELDS: NonNullable<ListMaterialFilterTypeWithValues['search']>['fields'] = [
  'name',
  'campanha',
  'code',
  'facilityName',
  'displayExternalId',
  'displayCode',
];

const CATALOG_FIELDS = [
  { param: 'material', label: 'Catálogo de Materiais' },
  { param: 'brand', label: 'Marcas' },
  { param: 'state', label: 'Estado' },
] as const;

type CatalogParam = (typeof CATALOG_FIELDS)[number]['param'];

export type MaterialListUrlState = {
  search: string;
  material?: string;
  brand?: string;
  state?: string;
  status?: string;
  page: number;
};

export type CatalogOptions = Record<CatalogParam, string[]>;

export function readListState(params: URLSearchParams): MaterialListUrlState {
  const page = Number(params.get('page'));
  return {
    search: params.get('q') ?? '',
    material: params.get('material') ?? undefined,
    brand: params.get('brand') ?? undefined,
    state: params.get('state') ?? undefined,
    status: params.get('status') ?? undefined,
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

export function writeListState(state: MaterialListUrlState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.search) params.set('q', state.search);
  for (const { param } of CATALOG_FIELDS) {
    const value = state[param];
    if (value) params.set(param, value);
  }
  if (state.status) params.set('status', state.status);
  if (state.page > 1) params.set('page', String(state.page));
  return params;
}

export function validateListState(state: MaterialListUrlState, options: CatalogOptions) {
  const valid = { ...state };
  const rejected: string[] = [];
  for (const { param, label } of CATALOG_FIELDS) {
    const value = state[param];
    if (value !== undefined && !options[param].includes(value)) {
      valid[param] = undefined;
      rejected.push(label);
    }
  }
  if (valid.status !== undefined && !STATUS_GROUPS.some((group) => group.key === valid.status)) {
    valid.status = undefined;
  }
  return { valid, rejected };
}

export function toFilterValues(state: MaterialListUrlState): ListMaterialFilterTypeWithValues {
  const filter: NonNullable<ListMaterialFilterTypeWithValues['filter']> = [];
  if (state.material) filter.push({ field: { name: 'material', condition: 'equals', value: state.material } });
  if (state.brand) filter.push({ field: { name: 'brand', condition: 'equals', value: state.brand } });
  const group = STATUS_GROUPS.find((g) => g.key === state.status);
  if (group) filter.push({ field: { name: 'status', condition: 'in', value: group.values } });
  if (state.state) filter.push({ field: { name: 'state', condition: 'equals', value: state.state } });
  return {
    ...(state.search && { search: { fields: SEARCH_FIELDS, value: state.search } }),
    ...(filter.length > 0 && { filter }),
  };
}

export function fromFilterValues(values: ListMaterialFilterTypeWithValues): MaterialListUrlState {
  const read = (name: string) => values.filter?.find((f) => f.field.name === name)?.field.value as string | undefined;
  const statusValue = values.filter?.find((f) => f.field.name === 'status')?.field.value;
  const group = STATUS_GROUPS.find((g) => JSON.stringify(g.values) === JSON.stringify(statusValue));
  return {
    search: values.search?.value ?? '',
    material: read('material'),
    brand: read('brand'),
    state: read('state'),
    status: group?.key,
    page: 1,
  };
}
